import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, Crown, Zap, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fetchMiMembresia, checkoutMembresia, pollEstadoPago, fetchMembershipBenefits } from "../lib/pagos";

// Solo estilos visuales por plan (íconos/colores). Los BENEFICIOS llegan del backend.
const ESTILO = {
  basico: { Icon: Star, color: "#64748B", destacado: false },
  pro: { Icon: Zap, color: "#2563EB", destacado: true },
  elite: { Icon: Crown, color: "#7E22CE", destacado: false },
};

export default function MembresiasPage() {
  const [params, setParams] = useSearchParams();
  const [planActual, setPlanActual] = useState("basico");
  const [planes, setPlanes] = useState([]);
  const [cargandoPlanes, setCargandoPlanes] = useState(true);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [verificando, setVerificando] = useState(false);

  async function cargar() {
    try {
      const m = await fetchMiMembresia();
      setPlanActual(m.plan || "basico");
    } catch (err) { console.error("[Membresías] no se pudo cargar la membresía actual:", err); }
  }

  async function cargarBeneficios() {
    setCargandoPlanes(true);
    try {
      const d = await fetchMembershipBenefits();
      setPlanes(d.planes || []);
    } catch (err) {
      console.error("[Membresías] no se pudieron cargar los beneficios:", err);
      toast.error("No se pudieron cargar los beneficios. Intenta recargar.");
    } finally { setCargandoPlanes(false); }
  }

  useEffect(() => {
    cargar();
    cargarBeneficios();
    const sid = params.get("session_id");
    if (sid) {
      setVerificando(true);
      pollEstadoPago(sid, () => {})
        .then((d) => {
          if (d.payment_status === "paid") { toast.success("¡Pago exitoso! Tu membresía fue actualizada."); cargar(); }
          else if (d.payment_status === "timeout") { toast.info("Estamos confirmando tu pago. Se actualizará en breve."); }
          else { toast.error("El pago no se completó."); }
        })
        .finally(() => { setVerificando(false); setParams({}, { replace: true }); });
    }
    // eslint-disable-next-line
  }, []);

  async function suscribir(plan) {
    setLoadingPlan(plan);
    try {
      const { url } = await checkoutMembresia(plan);
      window.location.href = url;
    } catch (e) {
      toast.error("No se pudo iniciar el pago. Intenta nuevamente.");
      setLoadingPlan(null);
    }
  }

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="membresias-page">
      <header className="text-center mb-10">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Membresías ConstruLink</h1>
        <p className="mt-2 text-slate-500 max-w-xl mx-auto">Elige el plan que impulsa tu trabajo en el Ecosistema. Cancela cuando quieras.</p>
        {verificando && (
          <div className="mt-4 inline-flex items-center gap-2 text-sm text-brand-blue" data-testid="pago-verificando">
            <Loader2 size={16} className="animate-spin" /> Verificando tu pago…
          </div>
        )}
      </header>

      {cargandoPlanes ? (
        <div className="flex items-center justify-center py-20 text-slate-400" data-testid="membresias-loading">
          <Loader2 size={26} className="animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {planes.map((p) => {
            const est = ESTILO[p.id] || ESTILO.basico;
            const Icon = est.Icon;
            const color = est.color;
            const actual = planActual === p.id;
            return (
              <div
                key={p.id}
                data-testid={`plan-card-${p.id}`}
                className={`relative bg-white rounded-2xl border p-6 flex flex-col ${est.destacado ? "border-brand-blue shadow-lifted md:-translate-y-2" : "border-slate-100 shadow-soft"}`}
              >
                {est.destacado && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-blue text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                    Más popular
                  </span>
                )}
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${color}1a`, color }}>
                  <Icon size={24} />
                </div>
                <h3 className="text-xl font-bold text-slate-900">{p.nombre}</h3>
                <p className="mt-1 mb-5">
                  <span className="text-3xl font-extrabold text-slate-900">{p.precio_label}</span>
                  {p.periodo && <span className="text-sm text-slate-400 font-medium">{p.periodo}</span>}
                </p>

                <div className="space-y-4 flex-1" data-testid={`plan-beneficios-${p.id}`}>
                  {p.grupos.map((g) => (
                    <div key={g.pilar}>
                      <p className="text-[11px] font-bold uppercase tracking-wider mb-1.5" style={{ color }}>{g.pilar}</p>
                      <ul className="space-y-1.5">
                        {g.beneficios.map((b) => (
                          <li key={b} className="flex items-start gap-2 text-sm text-slate-600">
                            <Check size={16} className="flex-shrink-0 mt-0.5" style={{ color }} />
                            {b}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                <div className="mt-6">
                  {actual ? (
                    <div className="w-full py-2.5 rounded-xl bg-slate-100 text-slate-500 font-semibold text-center text-sm" data-testid={`plan-actual-${p.id}`}>
                      Tu plan actual
                    </div>
                  ) : p.id === "basico" ? (
                    <div className="w-full py-2.5 rounded-xl border border-slate-200 text-slate-400 font-semibold text-center text-sm">
                      Incluido
                    </div>
                  ) : (
                    <button
                      onClick={() => suscribir(p.id)}
                      disabled={loadingPlan === p.id}
                      data-testid={`plan-suscribir-${p.id}`}
                      className="w-full py-2.5 rounded-xl text-white font-bold text-sm inline-flex items-center justify-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-60"
                      style={{ backgroundColor: color }}
                    >
                      {loadingPlan === p.id ? <Loader2 size={16} className="animate-spin" /> : null}
                      {planActual !== "basico" && p.id === "pro" ? "Cambiar a Pro" : `Suscribirme a ${p.nombre}`}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <p className="text-center text-xs text-slate-400 mt-8">
        Pagos procesados de forma segura con Mercado Pago · Modo de prueba activo.
      </p>
    </div>
  );
}
